import * as _ from "lodash";
import pathToRegexp from "path-to-regexp";
import * as React from "react";
import { useMappedState } from "redux-react-hook";

import tokenizeString from "src/library/utils/string-manipulations/tokenize-string";
import type { IStore } from "src/core/shared/store/index";
import { IRoute } from "src/pages/Router/routes";

import { MenuContext } from "./useMenu";

const useMenuItems = (): IRoute[] => {
  const {
    terminology, customers, users, notifications, products, drops, orders, lists
  } = useMappedState(
    ({
      global, customers, users, notifications, products, drops, orders, lists
    }: IStore) => ({
      role: global.role,
      error: global.error,
      customers,
      users,
      notifications,
      products,
      drops,
      orders,
      lists,
      erp: global.settings && global.settings.erp.name,
      installedModules: global.settings ? global.settings.installedModules || [] : [],
      terminology: global.settings && global.settings.terminology
    })
  );

  const [
    {
      routes, menuType, context
    }
  ] = React.useContext(MenuContext);

  const getPathWithData = React.useCallback(
    (path: string) => {
      const keys: pathToRegexp.Key[] = [];

      pathToRegexp(path, keys);
      const toPath = pathToRegexp.compile(path);
      const params: any = {};
      let error = false;

      _.each(keys, key => {
        switch (key.name) {
          case "customerId": {
            const customerId = drops.drop?.customerId || customers.customer?.id;

            if (customerId) {
              params[ key.name ] = customerId;
            } else {
              error = true;
            }
            break;
          }
          case "orderId":
            if (orders.currentOrder.data) {
              params[ key.name ] = orders.currentOrder.data.id;
            } else {
              error = true;
            }

            break;
          case "listId":
            if (lists.currentList.data) {
              params[ key.name ] = lists.currentList.data.id;
            } else {
              error = true;
            }

            break;
          case "userId":
            if (users.user) {
              params[ key.name ] = users.user.id;
            } else {
              error = true;
            }

            break;
          case "dropId":
            if (drops.drop) {
              params[ key.name ] = drops.drop.id;
            } else {
              error = true;
            }

            break;
          case "notificationId":
            if (notifications.notification) {
              params[ key.name ] = notifications.notification.id;
            }
            break;
          case "productId":
            if (products.product) {
              params[ key.name ] = products.product.id;
            }
            break;

          case "priceListId":
            if (products.priceLists && products.priceLists.priceListId) {
              params[ key.name ] = products.priceLists.priceListId;
            }
            break;

          default:
            break;
        }
      });

      if (error) {
        return null;
      } else if (Object.keys(params).length) {
        return toPath(params);
      } else {
        return path;
      }
    },
    [
      orders.currentOrder.data,
      lists.currentList.data,
      users.user,
      drops.drop,
      notifications.notification,
      products.product,
      products.priceLists,
      customers.customer?.id
    ]
  );

  return React.useMemo(() => {
    if (!terminology) {
      return [];
    }

    const items = _.map(
      _.filter(routes, route => {
        if (route.menuType === "EMPTY" || route.menuType === "NONE") {
          return false;
        }

        if (context.length) {
          if (route.context !== context[ 0 ].type && (!route.allowedContexts || !route.allowedContexts.includes(context[ 0 ].type))) {
            return false;
          } else {
            const contextMenu = route?.contextMenu?.[ context[ 0 ].type ];

            if (contextMenu) {
              if (contextMenu.hidden) {
                return false;
              } else {
                return menuType === route.menuType;
              }
            }
          }
        }

        if (route.hidden) {
          return false;
        }

        return menuType === route.menuType;
      }),
      routeItem => {
        // context nested children show
        if (routeItem.contextMenu && context[ 0 ] && routeItem.contextMenu[ context[ 0 ].type ]) {
          routeItem = {
            ...routeItem,
            ...routeItem.contextMenu[ context[ 0 ].type ]
          };
        }

        routeItem.title = tokenizeString(routeItem.menuTitle || routeItem.title, terminology);

        if (routeItem.path) {
          const path = getPathWithData(routeItem.path);

          if (routeItem.disabled) {
            routeItem.loadedPath = "/";
          }

          if (path) {
            routeItem.loadedPath = path;
          } else {
            routeItem.loadedPath = undefined;
          }
        }

        return routeItem;
      }
    );

    return items;
  }, [
    terminology,
    routes,
    context,
    menuType,
    getPathWithData
  ]);
};

export default useMenuItems;
